import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { formatBalance } from "@/lib/format";
import { Field, METHODS, todayIso } from "@/lib/finance";
import SearchPicker, { fetchCustomers } from "@/components/sales/SearchPicker";

const sel = "h-9 w-full px-2 rounded-md border border-slate-200 bg-white text-sm";

// order: { id, order_no, invoice_no } → söz bu satışa bağlanır (cari sabit). Söz muhasebe kaydı oluşturmaz.
export default function PromiseDialog({ customer: preset, order, initialAmount, onClose, onDone }) {
  const [customer, setCustomer] = useState(preset || null);
  const [f, setF] = useState({ amount: initialAmount ? String(initialAmount) : "", currency: "TRY", date: todayIso(), method: "", contact: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/odeme-sozleri", { customer_id: customer.id, amount: Number(f.amount), currency: f.currency, promise_date: f.date,
        planned_method: f.method || null, contact_person: f.contact || null, note: f.note || null, order_id: order?.id || null });
      toast.success(`${data.promise_no} kaydedildi`);
      onDone(data);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="promise-dialog">
        <DialogHeader><DialogTitle>{order ? `Ödeme Sözü · ${order.invoice_no || order.order_no}` : "Yeni Ödeme Sözü"}</DialogTitle></DialogHeader>
        {!customer ? (
          <SearchPicker testid="promise-customer-search" placeholder="Cari ara" fetcher={fetchCustomers} onPick={setCustomer}
            render={(c) => <div><div className="text-[13px] font-medium">{c.title}</div><div className="text-[11px] font-mono text-slate-400">{c.code} · {formatBalance(c.balance)}</div></div>} />
        ) : (
          <div className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 bg-slate-50" data-testid="promise-selected-customer">
            <div><div className="font-semibold text-sm">{customer.title}</div><div className="text-[11px] font-mono text-slate-500">{customer.code} · {formatBalance(customer.balance)}{order ? ` · ${order.order_no}` : ""}</div></div>
            {!preset && <button type="button" onClick={() => setCustomer(null)} className="text-slate-400 hover:text-rose-600" data-testid="promise-clear-customer"><X className="h-4 w-4" /></button>}
          </div>
        )}
        {customer && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <Field label="Tutar"><Input type="number" step="0.01" min="0.01" required value={f.amount} onChange={set("amount")} data-testid="promise-amount" /></Field>
            <Field label="Para Birimi"><select className={sel} value={f.currency} onChange={set("currency")} data-testid="promise-currency">{["TRY", "USD", "EUR", "CNY"].map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Söz Tarihi"><Input type="date" required min={todayIso()} value={f.date} onChange={set("date")} data-testid="promise-date" /></Field>
            <Field label="Planlanan Ödeme"><select className={sel} value={f.method} onChange={set("method")} data-testid="promise-method"><option value="">—</option>{Object.entries(METHODS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
            <Field label="Görüşülen Kişi" className="col-span-2"><Input value={f.contact} maxLength={120} onChange={set("contact")} placeholder="Opsiyonel" data-testid="promise-contact" /></Field>
            <Field label="Not" className="col-span-2"><Input value={f.note} maxLength={500} onChange={set("note")} placeholder="Örn. telefonda söz verdi, havale yapacak" data-testid="promise-note" /></Field>
            {f.currency !== "TRY" && <p className="col-span-2 text-[11px] text-amber-800" data-testid="promise-fx-note">Döviz sözler tahsilatla otomatik eşleşmez; “Ödendi işaretle” ile kapatılır.</p>}
            <p className="col-span-2 text-[11px] text-slate-500">Ödeme sözü muhasebe kaydı oluşturmaz, cari bakiyeyi değiştirmez.</p>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={onClose} data-testid="promise-cancel">Vazgeç</Button>
              <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="promise-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Kaydet</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
